import React from 'react';
import clsx from 'clsx';
import {
  NavLink,
  matchPath,
  useLoaderData,
  useLocation,
  useRouteLoaderData,
  Navigate,
  useOutlet,
  useNavigate
} from 'react-router-dom';

import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Button
} from '@headlessui/react';
import { Bars3Icon, BellIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { Await, TransitionDiv } from '@oysterjs/ui/async';
import { OysterLogo } from '@oysterjs/ui/logo';
import { Spinner } from '@oysterjs/ui/spinner';

import {
  AccountUserFieldsFragment,
  SignOutDocument,
  SignOutMutation,
  SignOutMutationVariables
} from '../api/graphql';
import { AccountContext, useAccountId } from '../api/context';
import { AnimatePresence } from 'motion/react';
import { client } from '../api/client';

const navigation = [
  { name: 'Policies', hrefs: ['policies', 'policies/:id'] },
  { name: 'Certificates', hrefs: ['certificates', 'certificates/:id'] },
  { name: 'Service Requests', hrefs: ['servicing', 'servicing/:id'] }
];
const userNavigation = [
  // { name: 'Your Profile', href: '/profile' },
  // { name: 'Settings', href: '/settings' },
  { name: 'Sign out', href: '/signout' }
];

const AccountSelectionWrapper = (props: React.PropsWithChildren) => {
  const { accountId, setAccountId } = useAccountId();
  const location = useLocation();
  const loader = useRouteLoaderData('root') as {
    data: Promise<{ accountUser: AccountUserFieldsFragment }>;
  };

  return (
    <React.Suspense>
      <Await resolve={loader.data}>
        {({ accountUser }) => {
          if (!accountId) {
            setAccountId(accountUser?.accounts?.[0]?.id || null);
            return <Navigate to="/policies" replace />;
          } else if (location.pathname === '/' || location.pathname === '') {
            return <Navigate to="/policies" replace />;
          }

          return props.children;
        }}
      </Await>
    </React.Suspense>
  );
};

export const DashboardContainer = () => {
  const [accountId, _setAccountId] = React.useState<string | null>(
    window.sessionStorage.getItem('_accountid') || null
  );

  const navigate = useNavigate();
  const loader = useLoaderData() as { data: Promise<{ accountUser: AccountUserFieldsFragment }> };
  const outlet = useOutlet();
  const location = useLocation();
  const currentPath = navigation.find((item) =>
    item.hrefs.some((href) => matchPath(href, location.pathname))
  );
  const title = currentPath?.name;

  const setAccountId = (id: string | null) => {
    _setAccountId(id);
    if (id) {
      window.sessionStorage.setItem('_accountid', id);
    } else {
      window.sessionStorage.removeItem('_accountid');
    }
  };

  const signOut = async () => {
    const res = await client.mutate<SignOutMutation, SignOutMutationVariables>({
      mutation: SignOutDocument
    });
    if (res.data?.accountUserSignOut) {
      navigate('/signin');
    }
  };

  return (
    <AccountContext.Provider value={{ accountId, setAccountId }}>
      <div className="min-h-full">
        <div className="bg-neutral-800 pb-32">
          <Disclosure as="nav" className="bg-neutral-800">
            <div className="mx-auto max-w-7xl sm:px-8">
              <div className="border-b border-neutral-700">
                <div className="flex h-16 items-center justify-between px-6 sm:px-0">
                  <div className="flex items-center">
                    <div className="shrink-0">
                      <OysterLogo scale={0.6} />
                    </div>
                    <div className="hidden md:block">
                      <div className="ml-10 flex items-baseline space-x-4">
                        {navigation.map((item) => (
                          <NavLink
                            key={item.name}
                            to={item.hrefs[0]}
                            className={({ isActive }) =>
                              clsx(
                                isActive
                                  ? 'bg-neutral-900 text-white'
                                  : 'text-neutral-300 hover:bg-neutral-700 hover:text-white',
                                'rounded-md px-3 py-2 text-sm font-medium'
                              )
                            }
                          >
                            {item.name}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <div className="ml-4 flex items-center md:ml-6">
                      <button
                        type="button"
                        className="relative rounded-full bg-neutral-800 p-1 text-neutral-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-neutral-800"
                      >
                        <span className="absolute -inset-1.5" />
                        <span className="sr-only">View notifications</span>
                        <BellIcon aria-hidden="true" className="size-6" />
                      </button>

                      {/* Profile dropdown */}
                      <React.Suspense fallback={<></>}>
                        <Await resolve={loader.data}>
                          {({ accountUser }) =>
                            accountUser && (
                              <Menu as="div" className="relative ml-3">
                                <div>
                                  <MenuButton className="relative flex max-w-xs items-center rounded-full bg-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-neutral-800">
                                    <span className="absolute -inset-1.5" />
                                    <span className="sr-only">Open user menu</span>
                                    <div className="size-8 rounded-full flex items-center justify-center font-semibold text-white bg-neutral-700">
                                      {accountUser.name
                                        .split(' ')
                                        .map((name) => name[0])
                                        .join('')
                                        .toUpperCase()}
                                    </div>
                                  </MenuButton>
                                </div>
                                <MenuItems
                                  transition
                                  className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                                >
                                  {userNavigation.map((item) =>
                                    item.href === '/signout' ? (
                                      <MenuItem key={item.name}>
                                        <Button
                                          onClick={signOut}
                                          className="block px-4 py-2 w-full text-left text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:outline-none"
                                        >
                                          {item.name}
                                        </Button>
                                      </MenuItem>
                                    ) : (
                                      <MenuItem key={item.name}>
                                        <NavLink
                                          to={item.href}
                                          className="block px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:outline-none"
                                        >
                                          {item.name}
                                        </NavLink>
                                      </MenuItem>
                                    )
                                  )}
                                </MenuItems>
                              </Menu>
                            )
                          }
                        </Await>
                      </React.Suspense>
                    </div>
                  </div>
                  <div className="-mr-2 flex md:hidden">
                    {/* Mobile menu button */}
                    <DisclosureButton className="group relative inline-flex items-center justify-center rounded-md bg-neutral-800 p-2 text-neutral-400 hover:bg-neutral-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-neutral-800">
                      <span className="absolute -inset-0.5" />
                      <span className="sr-only">Open main menu</span>
                      <Bars3Icon
                        aria-hidden="true"
                        className="block size-6 group-data-[open]:hidden"
                      />
                      <XMarkIcon
                        aria-hidden="true"
                        className="hidden size-6 group-data-[open]:block"
                      />
                    </DisclosureButton>
                  </div>
                </div>
              </div>
            </div>

            <DisclosurePanel className="border-b border-neutral-700 md:hidden">
              <div className="space-y-1 px-2 py-3 sm:px-3">
                {navigation.map((item) => (
                  <DisclosureButton
                    key={item.name}
                    as={NavLink}
                    to={item.hrefs[0]}
                    className={({ isActive }) =>
                      clsx(
                        isActive || currentPath?.name === item.name
                          ? 'bg-neutral-900 text-white'
                          : 'text-neutral-300 hover:bg-neutral-700 hover:text-white',
                        'block rounded-md px-3 py-2 text-base font-medium'
                      )
                    }
                  >
                    {item.name}
                  </DisclosureButton>
                ))}
              </div>
              <div className="border-t border-neutral-700 pb-3 pt-4">
                <React.Suspense fallback={<></>}>
                  <Await resolve={loader.data}>
                    {({ accountUser }) =>
                      accountUser && (
                        <div className="flex items-center px-5">
                          <div className="shrink-0">
                            <div className="size-10 rounded-full flex items-center justify-center font-semibold text-white bg-neutral-700">
                              {accountUser.name
                                .split(' ')
                                .map((name) => name[0])
                                .join('')
                                .toUpperCase()}
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-base/5 font-medium text-white">
                              {accountUser.name}
                            </div>
                            <div className="text-sm font-medium text-neutral-400">
                              {accountUser.email}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="relative ml-auto shrink-0 rounded-full bg-neutral-800 p-1 text-neutral-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-neutral-800"
                          >
                            <span className="absolute -inset-1.5" />
                            <span className="sr-only">View notifications</span>
                            <BellIcon aria-hidden="true" className="size-6" />
                          </button>
                        </div>
                      )
                    }
                  </Await>
                </React.Suspense>
                <div className="mt-3 space-y-1 px-2">
                  {userNavigation.map((item) =>
                    item.href === '/signout' ? (
                      <DisclosureButton
                        key={item.name}
                        onClick={signOut}
                        className="block rounded-md px-3 py-2 w-full text-left text-base font-medium text-neutral-400 hover:bg-neutral-700 hover:text-white"
                      >
                        {item.name}
                      </DisclosureButton>
                    ) : (
                      <DisclosureButton
                        key={item.name}
                        as={NavLink}
                        to={item.href}
                        className="block rounded-md px-3 py-2 text-base font-medium text-neutral-400 hover:bg-neutral-700 hover:text-white"
                      >
                        {item.name}
                      </DisclosureButton>
                    )
                  )}
                </div>
              </div>
            </DisclosurePanel>
          </Disclosure>
          <header className="py-10">
            <div className="mx-auto max-w-7xl px-6 sm:px-8">
              <h1 className="text-3xl font-semibold tracking-tight text-white">{title}</h1>
            </div>
          </header>
        </div>

        <main className="-mt-32">
          <div className="mx-auto max-w-7xl px-6 sm:px-8 pb-16">
            <React.Suspense
              fallback={
                <TransitionDiv className="my-16 flex items-center justify-center">
                  <div className="rounded-lg bg-white p-10 shadow">
                    <Spinner size="size-10" color="neutral" />
                  </div>
                </TransitionDiv>
              }
            >
              <Await resolve={loader.data}>
                {() => (
                  <AnimatePresence mode="wait">
                    <AccountSelectionWrapper>
                      {outlet && React.cloneElement(outlet, {})}
                    </AccountSelectionWrapper>
                  </AnimatePresence>
                )}
              </Await>
            </React.Suspense>
          </div>
        </main>
      </div>
    </AccountContext.Provider>
  );
};
